import { type FormEvent, useState } from 'react';
import { ArtifactCard } from './ArtifactCard';
import { MysticInput } from './MysticInput';
import { PrimaryRitualButton } from './PrimaryRitualButton';

type LoginState = 'idle' | 'submitting' | 'sent' | 'waitlisted' | 'error';

interface LoginArtifactCardProps {
  onSubmit: (email: string) => Promise<{
    success: boolean;
    status?: 'waitlisted' | 'magic_link_sent';
    error?: string;
  }>;
}

/**
 * Diegetic login panel styled as an ancient artifact/scroll
 */
export function LoginArtifactCard({ onSubmit }: LoginArtifactCardProps) {
  const [email, setEmail] = useState('');
  const [state, setState] = useState<LoginState>('idle');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setState('submitting');
    setError(null);

    const result = await onSubmit(email);

    if (result.success) {
      setState(result.status === 'waitlisted' ? 'waitlisted' : 'sent');
    } else {
      setState('error');
      setError(result.error ?? 'An unknown error occurred');
    }
  };

  if (state === 'sent') {
    return <MagicLinkSentView email={email} onReset={() => setState('idle')} />;
  }

  if (state === 'waitlisted') {
    return <WaitlistView email={email} onReset={() => setState('idle')} />;
  }

  return (
    <div className="w-full max-w-sm">
      <ArtifactCard>
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="font-display text-2xl text-parchment mb-2">Enter Loremail</h1>
          <p className="text-mist text-sm">Provide your sigil to receive passage</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <MysticInput
            type="email"
            label="Your Sigil (Email)"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={state === 'error' ? (error ?? undefined) : undefined}
            disabled={state === 'submitting'}
            autoComplete="email"
            autoFocus
            required
          />

          <PrimaryRitualButton
            type="submit"
            isLoading={state === 'submitting'}
            loadingText="Sending summons..."
            disabled={!email.trim()}
          >
            Seek the Threshold
          </PrimaryRitualButton>
        </form>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-mist/70 text-xs">A passage scroll will be sent to your sigil</p>
        </div>
      </ArtifactCard>
    </div>
  );
}

/**
 * Confirmation view after magic link is sent
 */
function MagicLinkSentView({ email, onReset }: { email: string; onReset: () => void }) {
  return (
    <div className="w-full max-w-sm">
      <ArtifactCard>
        {/* Success icon */}
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 rounded-full bg-rune/10 border border-rune/30 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-rune"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          </div>
        </div>

        {/* Message */}
        <div className="text-center">
          <h2 className="font-display text-xl text-parchment mb-2">Check Your Scroll</h2>
          <p className="text-mist text-sm mb-4">A passage seal has been sent to</p>
          <p className="text-rune font-medium mb-6 break-all">{email}</p>
          <p className="text-mist/70 text-xs mb-6">
            The seal expires in 15 minutes. Check your spam folder if needed.
          </p>

          <button
            type="button"
            onClick={onReset}
            className="text-rune/70 hover:text-rune text-sm underline transition-colors"
          >
            Use a different sigil
          </button>
        </div>
      </ArtifactCard>
    </div>
  );
}

function WaitlistView({ email, onReset }: { email: string; onReset: () => void }) {
  return (
    <div className="w-full max-w-sm">
      <ArtifactCard>
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 rounded-full bg-rune/10 border border-rune/30 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-rune"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6v6l4 2M12 2a10 10 0 1010 10A10 10 0 0012 2z"
              />
            </svg>
          </div>
        </div>

        <div className="text-center">
          <h2 className="font-display text-xl text-parchment mb-2">You are on the waitlist</h2>
          <p className="text-mist text-sm mb-4">
            We have recorded your sigil and will invite you when the gates open.
          </p>
          <p className="text-rune font-medium mb-6 break-all">{email}</p>
          <p className="text-mist/70 text-xs mb-6">
            We will notify you by email as soon as access is granted.
          </p>

          <button
            type="button"
            onClick={onReset}
            className="text-rune/70 hover:text-rune text-sm underline transition-colors"
          >
            Use a different sigil
          </button>
        </div>
      </ArtifactCard>
    </div>
  );
}
